import { useContext, useEffect, useEffectEvent, useRef, type ReactNode } from 'react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import {
  RESULTS_LIST_SCROLLER_ATTR,
  ResultsListScrollContext,
  scrollParentOf,
} from './results_page/scroll_parent.js';

/** A raiz do observer: com `listScroll: 'own'` o `<main>` do `ResultsLayout` rola a
 *  lista; fora disso (host rolando, ou fora do layout) o scroller mais próximo. */
function observerRoot(node: HTMLElement, listScroll: 'host' | 'own' | null): HTMLElement | null {
  const ownScroller =
    listScroll === 'own' ? node.closest<HTMLElement>(`[${RESULTS_LIST_SCROLLER_ATTR}]`) : null;
  return ownScroller ?? scrollParentOf(node);
}

/**
 * O fim da lista pedindo a próxima página.
 *
 * `IntersectionObserver` e não evento de scroll: o painel tem contêiner próprio
 * (e o mapa rola junto), então o observador acerta o alvo sem o componente
 * precisar saber QUEM rola. A raiz é o scroller mais próximo (`scrollParentOf`), e
 * não a viewport: a margem de prefetch abaixo só vale contra a raiz, e dentro de
 * um scroller próprio (o `<main>` do `ResultsLayout` com `listScroll: 'own'`) a
 * viewport não veria a sentinela antes do recorte dele. Quem rola muda com a largura
 * sem remontar a sentinela, então o observer é refeito quando o modo do layout muda
 * (`ResultsListScrollContext`): uma raiz velha que deixou de recortar via a sentinela
 * fora de cena e pedia página sem rolagem. Quando o ambiente não o
 * tem (jsdom sem polyfill), o botão continua ali — ele é o caminho acessível de
 * teclado de qualquer jeito, e é por isso que a sentinela não é uma `div` vazia.
 */
export function LoadMoreSentinel({
  hasMore,
  loading,
  label,
  onLoadMore,
}: {
  readonly hasMore: boolean;
  readonly loading: boolean;
  readonly label?: string;
  readonly onLoadMore?: () => void;
}): ReactNode {
  const ref = useRef<HTMLDivElement>(null);
  const loadMore = useEffectEvent(() => {
    onLoadMore?.();
  });
  const listScroll = useContext(ResultsListScrollContext);

  useEffect(() => {
    const node = ref.current;
    if (!node || !hasMore || loading || typeof IntersectionObserver !== 'function')
      return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMore();
      },
      // `rootMargin` generoso, e não o default 0: a sentinela vive no fim EXATO
      // do scroll, e com browser zoom fracionário (dpr 0.9) num iframe
      // cross-origin a interseção de borda arredonda pra zero — o observer nunca
      // disparava mesmo com a lista rolada até o fim (visto no dev em
      // 2026-08-18, iframe transbordando 0.22px da viewport). A margem também é
      // prefetch: a próxima página parte ~duas linhas antes do fim, então quem
      // rola contínuo não vê o degrau.
      { root: observerRoot(node, listScroll), rootMargin: '160px 0px' },
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [hasMore, loading, listScroll]);

  // Quem decide se a sentinela existe é ela, e não o host: sem quem chamar (host
  // sem paginação) ou sem rótulo (um botão anônimo chega ao teclado e ao leitor
  // de tela como controle sem nome) ela não monta.
  if (!onLoadMore || !label || (!hasMore && !loading)) return null;
  return (
    <div ref={ref} className="iuk-load-more">
      <Button type="button" variant="outline" disabled={loading} onClick={onLoadMore}>
        {label}
      </Button>
    </div>
  );
}
